/**
 * メニュー出し分け (menu parity) のための「事実 (facts)」。
 *
 * 設計方針 (docs/server-consolidation-and-menu-parity-plan.md §3):
 *   「事実は各基盤の NaviInput で渡す。メニュー定義と出し分け述語は `@rt/global-navi` に
 *    単一配置する」。各基盤は事実だけを載せ、評価 (述語) は本パッケージが行うことで、
 *    QANT ホスト時と Karte ホスト時で同一の見え方を保証する。
 *
 * facts は platform ごとに分離しており (`NaviFacts = { karte?, qant? }`)、
 * condition 評価も platform 単位で有効化される: ある platform の facts が与えられない場合
 * (旧 backend / bootstrap fallback 等)、その platform の述語を含む condition は評価が
 * スキップされ、従来どおり product 単位の表示にフォールバックする
 * (`filterProducts` / `collectConditionPlatforms` 参照)。空 `{}` も未指定扱い。
 */

import type { QantProductKey } from './qant.js';

/**
 * ロールスコープ。v5 `role_scopes.json` のうち本パッケージのメニュー出し分けで参照するもの。
 * (例: `hasRole('VOC','view')` / `hasRole('ISSUES','view')` / `hasRole('RIGHTCONNECT','view')` / `hasRole('SPEAK','view')`)
 */
export type KarteRoleScope = 'VOC' | 'ISSUES' | 'RIGHTCONNECT' | 'SPEAK';

/** Karte 基盤が提供する事実 (`KarteNaviInput.facts`)。 */
export type KarteNaviFacts = {
  /**
   * プロジェクトの i18n スコープ。
   * `isGlobalEnterpriseScope === true` は v5 の「locale !== 'ja' な Global版企業スコープ」に相当する。
   * v5 `i18nFilters` の意味論を保つため、`i18nScope` condition は
   *   `show = !isGlobalEnterpriseScope || requiredScope`
   * で評価される (= ja プロジェクトでは scope 条件を無視して常に表示)。
   */
  i18n?: { isGlobalEnterpriseScope: boolean };
  /**
   * ロールスコープごとに付与されている権限レベルの一覧 (例: `{ VOC: ['view'], ISSUES: ['view','edit'] }`)。
   * `role` condition は対象 scope の配列に level が含まれるかで評価する (v5 `permission.hasRole`)。
   */
  roles?: Partial<Record<KarteRoleScope, readonly string[]>>;
  /**
   * product×option 契約。product 名 (v5 `PRODUCT_NAMES` 準拠の大文字。例 `ISSUES`) → option キー → 有効。
   * `option` condition は `options[product]?.[option] === true` で評価する
   * (v5 `productContractsFilter` の option 条件)。
   */
  options?: Partial<Record<string, Readonly<Record<string, boolean>>>>;
  /**
   * インストール済みプラグインの有効状態 (plugin id → 有効)。
   * `plugin` condition は `plugins[plugin] === true` で評価する (v5 `permission.hasInstalledPlugin`)。
   */
  plugins?: Readonly<Record<string, boolean>>;
};

/**
 * QANT 基盤が提供する事実 (`QantNaviInput.facts`)。
 * auth backend が AuthInfo (account / project role / group policy) から「事前計算」して載せる。
 * 評価器が auth ドメインの生構造 (role の意味・Group.Policy) を知らずに済むよう、
 * boolean に畳んだ事実のみを持つ (Karte facts が v5 permission を評価済み事実に落とすのと同じ方針)。
 */
export type QantNaviFacts = {
  /**
   * 社内アカウントか (RightTouch 社員 or Employee grant 保持者)。
   * `employee` condition が参照する (v1 ナビの `isSystemAdminOnly` = `account.isEmployee` 判定に相当)。
   */
  isEmployee: boolean;
  /**
   * product ごとの管理権限。v1 `HasProduct管理権限` 相当:
   *   `hasActiveSystemAdmin || ((role === 'admin' | 'member') && group.policy[product].isAdmin)`
   * `qantProductAdmin` condition は `productAdmin[product] === true` で評価する。
   */
  productAdmin: Partial<Record<QantProductKey, boolean>>;
  /**
   * product ごとの PII アクセス権限。v1 `AuthInfo.HasPiiAccessNow({ productKey })` 相当:
   *   `account.hasActivePiiAccess || group.policy[product].hasPiiAccess`
   * `qantProductPii` condition は `productPii[product] === true` で評価する。
   * (例: neuro の応対ログ/課題は全面 PII のためこの権限でメニューを出し分ける)
   */
  productPii: Partial<Record<QantProductKey, boolean>>;
  /**
   * Project `featureFlags.qantSmsEnabled === true` のときのみ true。
   * Debeso SMS メニュー等の出し分けに使う。未指定 / false は非表示。
   */
  qantSmsEnabled?: boolean;
};

/**
 * platform ごとに分離した facts。`GlobalNaviData.facts` に載り、
 * `normalize`/`filterProducts` が condition 評価に使う。
 */
export type NaviFacts = {
  karte?: KarteNaviFacts;
  qant?: QantNaviFacts;
};

/**
 * Karte facts に condition 評価に使える事実が 1 つでもあるか。
 * 空 `{}` や各フィールドが空のオブジェクトのみの場合は false (= 未指定扱い)。
 * platform 単位の all-or-nothing: false なら karte 系述語を含む condition はスキップされる。
 */
export const hasKarteNaviFacts = (facts?: KarteNaviFacts): facts is KarteNaviFacts => {
  if (!facts) return false;
  if (facts.i18n !== undefined) return true;
  if (facts.roles && Object.keys(facts.roles).length > 0) return true;
  if (facts.options && Object.keys(facts.options).length > 0) return true;
  if (facts.plugins && Object.keys(facts.plugins).length > 0) return true;
  return false;
};

/**
 * QANT facts が有効か。必須フィールドのみで構成されるため、存在 = 有効。
 */
export const hasQantNaviFacts = (facts?: QantNaviFacts): facts is QantNaviFacts => facts !== undefined;
